// The effect language.
//
// The engine never reads printed card text. Instead every card carries a list of
// structured abilities, built at asset-build time by tools/compile-effects.mjs
// and hand-authored in data/effects/ where the compiler cannot do the job. That
// split matters: prose parsing at runtime would make every card a potential
// source of nondeterminism, whereas data can be diffed, validated, and fixed
// without touching the engine.
//
// The vocabulary below is deliberately small. It covers what the corpus actually
// says most often rather than trying to express every card in the game; a card
// whose text does not fit stays vanilla-with-keywords until someone writes an
// override for it, which is a much better failure mode than a half-parsed
// ability that does the wrong thing silently.

import type {
  Phase,
  TurnEffect,
  SpendOnly, Domain, Keyword } from './types.ts';

// -- selectors ---------------------------------------------------------------

/**
 * Who or what an instruction applies to. `choose` selectors are targets and are
 * picked when the item is finalized on the chain (355); everything else is
 * resolved programmatically at resolution time and does not target (355.10.d).
 */
export type Selector =
  | { s: 'self' }
  | { s: 'chosen'; index?: number }
  | {
    s: 'choose'; of: ObjectFilter; count?: number; upTo?: boolean;
    /**
     * "any number of units with a total [M] of 8 or less" -- a cap on the
     * group, not on each member. Applied while picking, so the selection stops
     * before it goes over.
     */
    totalMightAtMost?: number;
    /**
     * "two units at the same battlefield" -- a constraint between the picks
     * rather than on each of them. Reading it as "at a battlefield" would be
     * looser than the card prints: the two could end up in different places.
     */
    sameLocation?: boolean;
    /**
     * "Up to one enemy unit at each location" -- a cap per place rather than
     * an overall count. Reading it as "one unit" would hit a single target,
     * and as "all units" would hit every one at every location; neither is
     * what the card says.
     */
    perLocation?: boolean;
  }
  | { s: 'all'; of: ObjectFilter }
  /**
   * 203.1. What the cost was paid with: "move a different unit you control to
   * the location of the unit you exhausted to pay for this ability". The
   * victim is picked while paying, after targets are settled, so it is not a
   * `chosen` referent and nothing else records it.
   */
  | { s: 'paidWith' }
  | { s: 'controller' }
  /** The owner of whatever was chosen -- "its owner channels 1 rune". */
  | { s: 'chosenOwner' }
  | { s: 'opponent' };

/** Restrictions on what a selector may pick. Every field is a conjunct. */
export interface ObjectFilter {
  /** 'unit' covers champion units and unit tokens (141). */
  type?: 'unit' | 'gear' | 'permanent' | 'spell' | 'rune' | 'battlefield' | 'legend';
  /** "a non-unit card" -- everything the type names, excluded. */
  notType?: 'unit' | 'gear' | 'spell' | 'rune' | 'battlefield' | 'legend';
  side?: 'friendly' | 'enemy' | 'any';
  /**
   * 'battlefield' means any battlefield; 'here' means this object's location;
   * 'elsewhere' means anywhere but this object's location, which is what
   * "at a different location" describes.
   */
  where?: 'battlefield' | 'base' | 'here' | 'anywhere' | 'elsewhere';
  /**
   * 108. Where the card is. `deck` is the Main Deck; `championZone` is the
   * face-up champion slot, which several cards move cards to and from and
   * which is not reachable any other way.
   */
  zone?: 'board' | 'hand' | 'trash' | 'deck' | 'championZone' | 'banishment';
  /** 447. "Spells banished with me" -- banished by this effect's own source. */
  banishedBySelf?: boolean;
  /**
   * 137. Held by whoever holds the referent: "choose a unit and an Equipment
   * with the same controller". A constraint between two picks, not a property
   * of either, so it can only be tested once the first one is settled.
   */
  sameControllerAsChosen?: boolean;
  /**
   * 206. "A unit that costs no more Energy and no more Power than the killed
   * unit." A comparison against the referent's printed cost, not a fixed
   * number -- which is why it cannot be `maxEnergyCost`.
   */
  costsNoMoreThanChosen?: boolean;
  /**
   * 143. "a unit that has [M] up to 1 more than the killed unit" -- a ceiling
   * set by the referent's Might plus a margin, rather than a fixed number.
   */
  maxMightOverChosen?: number;
  /**
   * 463. "A friendly unit that's in combat with an enemy Fury unit." Not the
   * same as `inShowdown`, which only asks whether this unit is in a combat --
   * this asks *who it is facing*, which is a fact about the other side.
   */
  inCombatWith?: ObjectFilter;
  /**
   * 152. "An occupied enemy battlefield if my Might is greater than the total
   * Might of enemy units there." A comparison between the source and everyone
   * standing at the place, which no per-object test can express.
   */
  weakerThanSourceTogether?: boolean;
  /**
   * 355.9. "... that's being chosen by an enemy Fury spell." The inverse of
   * `chooses`: that one asks what a chain item picked, this asks whether
   * anything on the chain picked *this*.
   */
  chosenBy?: ObjectFilter;
  /**
   * 327. Heads a pending chain item -- a spell waiting to resolve, or the
   * permanent whose activated ability is waiting.
   *
   * "An enemy spell **or ability**" cannot be a type filter: an ability's chain
   * item is headed by the unit or gear that owns it, which is an ordinary board
   * object of no particular type. What identifies it is the chain, not the card.
   */
  onChain?: 'spell' | 'ability' | 'any';
  /**
   * 355.9. "... that chooses a friendly unit or gear." A property of the chain
   * item's targets, which are only settled once it is on the chain -- nothing
   * about the card says what it will choose.
   */
  chooses?: ObjectFilter;
  /**
   * 355.9. "... that chooses it *and no other friendly unit*." The item may
   * pick the referent, and nothing else matching this -- a counter that only
   * answers a spell aimed at one unit.
   */
  choosesNoOther?: ObjectFilter;
  /** The referent itself: "that chooses **it**". */
  isChosen?: boolean;
  /**
   * 818. The unit this source is attached to. An Equipment's "I" and "me" mean
   * the equipped unit, not the Equipment, so its own text needs a way to name
   * that unit as a filter.
   */
  host?: boolean;
  minMight?: number;
  maxMight?: number;
  maxEnergyCost?: number;
  /**
   * "a gear with Energy cost no more than my [M]" -- a ceiling that changes as
   * the board does, so it is read at the moment the filter is applied.
   */
  maxEnergyCostFrom?: DynamicAmount;
  minEnergyCost?: number;
  /** 135.2.e. A ceiling on the printed Power pips. */
  maxPowerCost?: number;
  hasKeyword?: Keyword;
  /**
   * 135.2.e. "an enemy Chaos unit" -- the card's printed domain. A card can
   * have more than one, and this asks whether it has this one.
   */
  domain?: Domain;
  /** "a friendly unit without [Temporary]" -- the complement, not the absence. */
  lacksKeyword?: Keyword;
  tag?: string;
  /**
   * 133.8. "A unit with the named tag" -- the tag the ability's *source* named
   * as it was played, which is not knowable at compile time.
   *
   * A filter carrying this and no named tag on the source matches nothing,
   * rather than everything: an unnamed List should not debuff the board.
   */
  namedTag?: boolean;
  /**
   * 144. "A unit at a battlefield I moved to or from" -- either end of the
   * source's most recent move. Two places, so it cannot be said with `where`,
   * which names one.
   */
  atMoveEndpoints?: boolean;
  /** "another non-Recruit unit" -- a tag the object must NOT carry. */
  notTag?: string;
  /**
   * "a unit or a gear" -- matches when any of these do. The rest of the fields
   * still apply on top, so a union can be narrowed by side or location.
   */
  anyOf?: ObjectFilter[];
  /** Excludes the ability's own source. */
  other?: boolean;
  exhausted?: boolean;
  ready?: boolean;
  /** 460. Combat designations, for "if it's attacking, deal 4 to it instead". */
  attacking?: boolean;
  defending?: boolean;
  /** 818. Carrying at least one attachment. */
  equipped?: boolean;
  /**
   * 818. Whether the object is itself attached to something. Distinct from
   * `equipped`, which is the other end of the same relationship: a detached
   * Equipment is not attached, and a unit carrying it is equipped.
   */
  attached?: boolean;
  detached?: boolean;
  /**
   * 463. "A friendly unit in a showdown" -- attacking or defending. A
   * disjunction, so it cannot be written as the two flags, which are conjuncts.
   */
  inShowdown?: boolean;
  /**
   * 463. "while a friendly unit defends alone" -- the only defender on its
   * side. A comparison against the rest of the board, which no other field can
   * express.
   */
  defendsAlone?: boolean;
  /** 152. "a battlefield where you have units" -- occupied by the asker. */
  whereYouHaveUnits?: boolean;
  /** 152. "an occupied enemy battlefield" -- one with any unit standing at it. */
  occupied?: boolean;
  /**
   * 152. "a battlefield with exactly two units there" -- how many units stand
   * at the battlefield being described, which is a fact about the place rather
   * than about the card sitting in it.
   */
  unitsThere?: number;
  /**
   * 143.2. Might compared against the ability's own source rather than a fixed
   * number, which is what "with less [M] than me" asks.
   */
  lessMightThanSource?: boolean;
  /**
   * 143.2. The same comparison against whatever the effect has already chosen:
   * "kill an enemy unit with less [M] than it", "move all enemy units with
   * less [M] than the chosen unit". Needs the referent, which is why
   * `matchesFilter` takes one.
   */
  lessMightThanChosen?: boolean;
  /** 144. "an enemy unit at its location" -- where the referent is standing. */
  atChosenLocation?: boolean;
  /**
   * 152. "A location where there's a unit with the same controller." A
   * battlefield picked for who is standing on it rather than for anything the
   * battlefield card itself says.
   */
  whereUnitSharesControllerWithChosen?: boolean;
  /**
   * 463. "an enemy unit alone at that battlefield" -- the only unit standing
   * there, whoever controls the rest. A comparison against the board, which no
   * other field can express.
   */
  alone?: boolean;
  /** 705. Carrying damage that has not yet proved lethal. */
  damaged?: boolean;
  /** 186. Created by an effect rather than played from a deck. */
  isToken?: boolean;
  /** "a non-token unit" -- printed cards only, which is what those cards mean. */
  notToken?: boolean;
  /**
   * 103.2. "A champion" -- a unit carrying the Champion super-type. Riot marks
   * that tag on only a handful of legends (see the card-data notes), but it is
   * reliable on the unit cards themselves, which is what this asks about.
   */
  champion?: boolean;
  /**
   * 056. "A Teemo unit you own" -- ownership, not control. A card you own can
   * be under someone else's control, and these cards ask about the former.
   */
  ownedByYou?: boolean;
  /**
   * A specific printed card, by id. Tokens are named on the cards that make
   * them -- "a Shadow Clone you control" -- and a token's name is not a tag,
   * so it cannot be reached any other way.
   */
  card?: string;
  /**
   * "a card with my name in your trash". Matched on the printed name rather
   * than the card id, because alternate arts and signed printings are the same
   * card by name and different by id.
   */
  sameNameAsSource?: boolean;
  /** 349. Played from somewhere other than a hand -- trash, deck, facedown. */
  playedFromNonHand?: boolean;
  stunned?: boolean;
  buffed?: boolean;
  empowered?: boolean;
}

// -- instructions ------------------------------------------------------------

export type Duration = 'turn' | 'combat' | 'permanent';

/** A number read from the game state rather than printed on the card. */
/**
 * A number read from the game state rather than printed on the card.
 *
 * The named members cover quantities that are not a count of board objects
 * (points, XP, hand size). Anything that *is* such a count is expressed as a
 * filter instead, so "for each <anything>" works without a new member per
 * phrase the cards happen to use.
 */
export type DynamicAmount =
  | 'selfMight' | 'points' | 'xp' | 'handSize' | 'friendlyUnits' | 'enemyUnits'
  | 'mightyUnits' | 'buffedUnits' | 'friendlyRunes' | 'cardsInTrash'
  | 'battlefieldsControlled' | 'friendlyGear' | 'friendlyEquipment'
  | 'tokenUnits' | 'enemyUnitsHere' | 'friendlyUnitsHere'
  /** 705.4. Excess combat damage this player assigned this turn. */
  | 'excessDamage'
  /** What a preceding "pay any amount" actually paid. */
  | 'paidAmount'
  /** 705. Damage already marked on the chosen object. */
  | 'damageOnChosen'
  /** 143. The Might of whatever was chosen -- "+[M] equal to its [M]". */
  | 'chosenMight'
  /** 206. The printed Energy cost of the chosen card. */
  | 'chosenCost'
  /**
   * 206. The printed Energy cost of the chosen *spell*.
   *
   * Distinct from `chosenCost`, which reads whatever was chosen first: "choose
   * a friendly unit and a spell ... give that unit +[M] equal to that spell's
   * Energy cost" chooses the unit first, so reading the first pick would give
   * the unit's own cost.
   */
  | 'chosenSpellCost'
  /** 206. The printed Energy cost of the effect's own source. */
  | 'selfCost'
  /** "for each card you've played this turn". */
  | 'cardsPlayedThisTurn'
  /** 471. "for each point you scored from holding this turn". */
  | 'pointsHeldThisTurn'
  /** Board objects matching a filter, counted. */
  | { of: ObjectFilter }
  /**
   * "For each of the following tags among your units - Bird, Cat, Dog, and
   * Poro." Distinct tags present, not units: four Cats is one.
   */
  | { tagsAmong: string[] }
  /**
   * 706. "For each time this spell has dealt damage this turn." A count of the
   * source's own damage instances, which nothing on the board records.
   */
  | { selfDamageInstancesThisTurn: true }
  /** 818. Cards attached to the source. */
  | 'attachedToSelf'
  /** 441. "For each time I'm [Empowered]." */
  | 'selfEmpowerCount'
  /** 143. The largest Might among this player's units, not a count of them. */
  | 'highestFriendlyMight'
  /** "For each card with this name in your trash" -- other printings of me. */
  | 'sameNameInTrash';

/**
 * One instruction (135.2.b). Unknown ops are skipped by the interpreter rather
 * than throwing, so newer data can ship against an older engine safely.
 */
export type Instruction =
  | { op: 'draw'; count: number; who?: Selector; countFrom?: DynamicAmount }
  | {
    op: 'discard'; count: number; who?: Selector;
    /** "They discard that card" -- one already chosen, not any card they like. */
    target?: Selector;
  }
  /**
   * `amountFrom` makes the damage dynamic: "deal damage equal to my Might"
   * has to be read at resolution, not baked in when the card was compiled.
   */
  | {
    op: 'deal'; amount: number; target: Selector; amountFrom?: DynamicAmount;
    /**
     * 706. "This deals 1 Bonus Damage for each card with this name in your
     * trash." Extra damage this instruction carries, counted at resolution.
     * Not `bonusDamage`, which is an aura over everything a player deals.
     */
    bonusPer?: { amount: number; from: DynamicAmount };
  }
  /** 355.14. Split N damage among any number of chosen units. */
  | {
    op: 'dealSplit'; amount: number; target: Selector;
    /** "Damage equal to its Might, split" -- read at resolution, not compiled in. */
    amountFrom?: DynamicAmount;
  }
  | { op: 'heal'; target: Selector }
  /** 423. Binary status; a stunned unit contributes no Might to combat damage. */
  | { op: 'stun'; target: Selector }
  | { op: 'kill'; target: Selector }
  | { op: 'banish'; target: Selector }
  | {
    op: 'mightMod'; amount: number; target: Selector; duration: Duration;
    min?: number; amountFrom?: DynamicAmount;
  }
  | {
    op: 'grantKeyword'; keyword: Keyword; value?: number; target: Selector;
    duration: Duration;
    /** "[Flow] equal to its cost" -- read at resolution, not baked in. */
    valueFrom?: DynamicAmount;
  }
  /**
   * 806. "Give a friendly unit my keywords." Which keywords the source has is
   * only known on the board, so they are copied at resolution rather than
   * listed when the card was compiled.
   */
  | { op: 'grantMyKeywords'; target: Selector; duration: Duration }
  | { op: 'ready'; target: Selector }
  | { op: 'exhaust'; target: Selector }
  /**
   * 152.4. `battlefield` is an unnamed battlefield the mover picks; `here` is
   * the source's own location; `chosenLocation` is wherever an earlier choice
   * in the same sentence landed -- "move a friendly unit to that enemy unit's
   * battlefield".
   */
  | {
    op: 'move'; target: Selector;
    /**
     * 144. `battlefield` is any battlefield other than the one the unit is on,
     * which is what "a different battlefield" says out loud; `friendly` narrows
     * that to ones the effect's controller holds.
     */
    to: 'battlefield' | 'friendlyBattlefield' | 'base' | 'here' | 'chosenLocation'
      /**
       * 144. "... to a single location": every unit moved goes to the *same*
       * battlefield. `battlefield` picks one per unit, which would scatter a
       * group the card says arrives together.
       */
      | 'oneBattlefield'
      /**
       * 152. The battlefield the effect chose, as opposed to
       * `chosenLocation`, which is wherever the first chosen *object* happens
       * to be standing -- often the unit being moved.
       */
      | 'chosenBattlefield'
      /** 203.1. Where the thing the price consumed was standing. */
      | 'paidWithLocation'
      /**
       * 152. "To a battlefield where you have units" -- the mover's own
       * units, not the moved unit's. Distinct from `friendlyBattlefield`,
       * which is about who *controls* the battlefield: you can have units at
       * one you do not hold, and that is the whole point of dragging an enemy
       * onto it.
       */
      | 'occupiedBattlefield';
  }
  /**
   * 144.5. Sends a unit back to its base. Not a move, so nothing that watches
   * moving sees it. `exhausted` covers "recall it exhausted", which returns the
   * unit tapped rather than ready.
   */
  | { op: 'recall'; target: Selector; exhausted?: boolean }
  /** 429. Put resources into the controller's Rune Pool. */
  | {
    op: 'addResource'; energy?: number; power?: number;
    /** "Add that much energy" -- a count the board or the context supplies. */
    energyFrom?: DynamicAmount; powerFrom?: DynamicAmount;
    domain?: Domain | 'any' | 'self';
    /** "Use only to play spells." Narrows the Energy component (204). */
    only?: SpendOnly;
  }
  | { op: 'channel'; count: number; exhausted?: boolean; who?: Selector }
  /**
   * 322.4. A symmetrical effect: run the same instructions once per player,
   * as that player. Wrapping rather than teaching every op a "who" keeps
   * "each player discards their hand, then draws 3" a single composition.
   */
  | { op: 'eachPlayer'; then: Instruction[] }
  /**
   * 322. One player doing something, as them: "Choose an opponent. They play a
   * 1 [M] Bird unit token."
   *
   * Note this is not the "their" trap (see SIDE_WORDS): that one is possessive
   * and must never become a side. This is a subject the sentence before it
   * named, and the instructions run with that player as their controller.
   */
  | { op: 'asPlayer'; who: Selector; then: Instruction[] }
  /** 316.2. "Discard your hand" -- all of it, however large. */
  | { op: 'discardHand'; who?: Selector }
  /**
   * 447. "Banish your hand and trash." Whole zones at once, which is not a
   * `banish` with a filter: those select board objects, and these cards are
   * nowhere near the board.
   */
  | { op: 'banishZones'; zones: ('hand' | 'trash')[]; who?: Selector }
  | {
    op: 'recycle'; count: number; from: 'trash' | 'hand' | 'board';
    /** "They recycle that card" -- one already chosen, not a count off a zone. */
    target?: Selector;
  }
  /**
   * 375. "Put the top 2 cards of your Main Deck into your trash." Straight to
   * the trash, which is not recycling -- those go to the bottom of the deck.
   */
  | { op: 'mill'; count: number; who?: Selector }
  | { op: 'gainPoints'; amount: number; who?: Selector; amountFrom?: DynamicAmount }
  | { op: 'gainXp'; amount: number; amountFrom?: DynamicAmount }
  /** 426. Riftbound "buff": a +1 Might marker, at most one per unit. */
  | { op: 'buff'; target: Selector }
  | { op: 'spendBuff'; target: Selector }
  | {
    op: 'createToken'; token: string; count?: number; ready?: boolean;
    /**
     * 144. "Here" and "there" are not the same place.
     *
     * "Here" is where the source is. "There" is a place the sentence or the
     * trigger already named -- a battlefield an earlier instruction chose, or
     * the location a unit moved *from*. Lillia, Fae Fawn reads "when I move
     * from a location, play a Sprite token there", and collapsing the two put
     * the Sprite at her destination: she left her base and the token appeared
     * at the battlefield she had just walked into, which is the opposite of
     * what the card does.
     */
    where?: 'here' | 'base' | 'there';
    /** "A token for each Equipment you control" -- counted at resolution. */
    countFrom?: DynamicAmount;
  }
  | {
    op: 'counter'; target: Selector;
    /**
     * 355.12. "Counter a spell unless its controller pays [2]." The counter
     * only happens if the price is not met; nothing here can prompt, so the
     * controller pays whenever they are able to.
     */
    unlessPays?: { energy: number; power: number };
  }
  /** 436. Look at the top card; you may recycle it. */
  | { op: 'predict'; count?: number }
  /** 441 / 442. Empower and its inverse. */
  | { op: 'empower'; target: Selector }
  | { op: 'disempower'; target: Selector }
  /** 424. Reveal from the top of a deck; `keep` filters what may be taken. */
  | { op: 'reveal'; count: number; from?: 'deck' | 'hand'; who?: Selector }
  /**
   * 424. "Reveal cards from the top of your Main Deck until you reveal a unit."
   * The count is not known in advance, so it is a separate op rather than a
   * `reveal` with a dynamic count -- what stops it is a filter, not a number.
   */
  | { op: 'revealUntil'; filter: ObjectFilter }
  /**
   * 424. "Reveal the top rune of your Rune Deck, then recycle it." The Rune
   * Deck is a separate pile, and the rune becomes the referent so the branch
   * that follows can read its domain.
   */
  | { op: 'revealTopRune'; recycle?: boolean }
  /**
   * 133.8. "As you play this, name a tag" / "name a spell". The word is
   * recorded on the source permanent, where its other abilities read it.
   *
   * Every tag and every spell name printed in the game is offered, because
   * that is what the card allows -- narrowing the list to what is currently
   * visible would be a different, weaker card.
   */
  | { op: 'nameIt'; what: 'tag' | 'spell' }
  /**
   * 185. "Replace this with the battlefield it replaced." Undoes a
   * `replaceBattlefield`: the displaced card comes back to the same index and
   * the token that stood in for it leaves.
   */
  | { op: 'restoreBattlefield' }
  /**
   * 463. "When I attack, the defender must kill one of their units here."
   *
   * The choice belongs to the defender, not to the ability's controller, which
   * is why it is its own op rather than a `kill` with an enemy filter: a kill
   * aimed by the attacker would take the defender's best unit, and this takes
   * whichever one they are willing to lose.
   */
  | { op: 'defenderKills'; count?: number }
  /**
   * 356.4. "You may play this from your trash for [A]."
   *
   * Offered rather than done: the card is still resolving, and it reaches the
   * trash only when it finishes. So this leaves an offer behind that lasts the
   * turn, which `playableCards` reads once the card has landed.
   */
  | {
    op: 'mayPlaySelfFromTrash';
    price: { energy: number; power: number; domain?: Domain | 'any' };
    /**
     * Whose offer it is. "Its controller may play this spell again" hands the
     * replay to the player whose unit was hit, not to the caster.
     */
    who?: 'chosenController';
  }
  /** Takes one of the cards revealed by a preceding reveal. */
  | {
    op: 'takeRevealed'; filter?: ObjectFilter; to?: 'hand' | 'board';
    upTo?: boolean;
    /**
     * 349.4. Where a unit taken to the board lands. Base unless the card says
     * "here", which is the source's own battlefield.
     */
    where?: 'here';
    /** How many to take; one unless the card says otherwise. */
    count?: number;
    /**
     * 356. A card taken to the board is *played*, so it is paid for -- and a
     * card that says "reducing its cost by [2]" is saying what it is paid for
     * with the discount. `free` is the "ignoring its cost" wording.
     */
    discount?: { energy: number; power: number };
    free?: boolean;
  }
  /** Recycles whatever a preceding reveal left over. */
  | {
    op: 'recycleRevealed';
    /**
     * 424. "You may recycle one or both of them" -- asked per card, rather
     * than recycling everything the look turned up.
     *
     * Without this the card read as doing nothing at all: both cards went to
     * the bottom of the deck silently, the player was never asked, and "put
     * those you don't back" had nothing left to put back.
     */
    may?: boolean;
  }
  /**
   * 416. "Recycle the rest" said of a *keep*, not of a look: everything
   * matching that this player did not choose. Held apart from
   * `recycleRevealed` deliberately -- that one reads the reveal list, which a
   * card like this never fills, so it would recycle nothing at all.
   */
  | { op: 'unchosen'; filters: ObjectFilter[]; action: 'recycle' | 'kill' }
  /**
   * 424.2. "Put those you don't back in any order." A reveal does not move
   * cards, so the leftovers are already on the deck -- what this settles is
   * that nothing later may take them. Written out rather than dropped, so the
   * sentence is accounted for instead of silently ignored.
   */
  | { op: 'keepRevealed' }
  /** 416. "Put the rest into your trash" -- the leftovers of a look, discarded. */
  | { op: 'trashRevealed' }
  /**
   * 424. "Choose a card from it, and they discard that card." One card out of
   * a revealed hand -- so `count` picks rather than taking everything, and
   * `filter` is what the card is allowed to be.
   */
  | {
    op: 'pickRevealed'; filter?: ObjectFilter; count?: number;
    /**
     * 424. Take the pick out of the revealed set as well as naming it.
     *
     * "Choose one, then recycle the rest" -- without this the chosen card is
     * still revealed when "the rest" is swept up, and the card the player just
     * picked goes back into the deck with everything else.
     */
    setAside?: boolean;
    /**
     * `chosen` picks one out of the look without moving it, so the sentences
     * that follow can name it -- "you choose a Mind card from it. They recycle
     * that card" is two instructions about one card.
     */
    to: 'trash' | 'recycle' | 'banishment' | 'chosen';
  }
  | { op: 'returnToHand'; target: Selector }
  /** 419. Play a card out of a zone, optionally without paying. */
  | {
    op: 'playFrom'; zone: 'hand' | 'trash' | 'deck' | 'banishment';
    filter?: ObjectFilter;
    free?: boolean; optional?: boolean;
    /** "Play *all* units banished with this" -- every match, not the first. */
    all?: boolean;
    /**
     * 356.4. "You may play me for [A]" -- a price that replaces the printed
     * one. Distinct from `free`, which pays nothing at all.
     */
    forCost?: { energy: number; power: number; domain?: Domain | 'any' };
    /** 356. "... reducing its cost by [3]" -- off the printed price, not instead of it. */
    discount?: { energy: number; power: number };
    /**
     * 356. "Reduce its Energy cost by the Might of the unit you recycled." The
     * discount is read at resolution from a referent, so it cannot be a number
     * settled when the card was compiled.
     */
    discountFrom?: DynamicAmount;
    /**
     * 349.4. Where a unit played this way lands: a battlefield the player
     * controls, or the source's own place when the card says "here".
     */
    to?: 'friendlyBattlefield' | 'here';
    /**
     * 353. "Recycle that spell after you play it." What happens to the card
     * once it has been played, which is a property of this play rather than a
     * separate instruction -- nothing else knows which card it was.
     */
    after?: 'recycle' | 'banish';
  }
  /**
   * 349. "Banish a friendly unit, then its owner plays it, ignoring its cost."
   *
   * A blink: the card comes straight back onto the board under its *owner's*
   * control, not the effect controller's, which is what "its owner" says.
   * `where` is where it lands -- back where it was, or at a fresh battlefield.
   */
  | {
    op: 'ownerPlays'; target?: Selector;
    /**
     * 356. "Ignoring Energy costs. (They must still pay Power costs.)" The
     * usual `ownerPlays` puts the card down for nothing; this one still
     * charges the Power half, and the card is not played if it cannot be paid.
     */
    payPower?: boolean;
    /**
     * `chosenBattlefield` is the battlefield an earlier instruction picked, as
     * opposed to `battlefield`, which is any of them: "choose a battlefield
     * ... they play that unit to *that* battlefield".
     */
    where: 'same' | 'battlefield' | 'base' | 'chosenBattlefield';
  }
  /** 375. "Its owner places it on the top or bottom of their Main Deck." */
  | { op: 'ownerPlaces'; target?: Selector; to: 'top' | 'bottom' }
  /**
   * 108. Moves a card to one of its owner's private zones: "return your chosen
   * champion from your trash to your Champion Zone".
   */
  | {
    op: 'moveCard'; target: Selector;
    to: 'hand' | 'trash' | 'championZone' | 'deckTop' | 'deckBottom';
  }
  | { op: 'moveAllCards'; filter: ObjectFilter; to: 'hand' | 'trash' }
  /** 434. Attach this to a unit (Equip, Quick-Draw). */
  /**
   * 818. Attaches `what` to `target`. `what` defaults to the source, which is
   * how Equip reads; "if it's an Equipment, attach it to me" is the other way
   * round, attaching the chosen card to the source.
   */
  | { op: 'attach'; target: Selector; what?: Selector }
  /** 818. "Then detach an Equipment from it." The opposite of `attach`. */
  | {
    op: 'detach'; from: Selector; what?: ObjectFilter;
    /**
     * 818. Said from the attachment's side rather than the host's: "unattach
     * this" is `{ of: self }` and "if it's an Equipment, detach it" is
     * `{ of: chosen }`. `from` names a host and takes things off it; `of`
     * names the attachments themselves and takes them off whatever they are on.
     */
    of?: Selector;
  }
  /**
   * 821.1.c. Weaponmaster: attach one of your Equipment to the source by
   * paying that Equipment's *own* Equip cost, reduced by `discount` Power.
   *
   * Its own op because the price is not on this card -- it is read off
   * whichever Equipment is chosen, which nothing else in the instruction
   * vocabulary can express.
   */
  | { op: 'equipDiscounted'; discount: number }
  /**
   * 144. Exchanges where two objects stand. "Move me to its location and it to
   * my original location" is one action, not two moves: doing it as two would
   * read the second object's location after the first had already left.
   */
  | { op: 'swapLocations'; a: Selector; b: Selector }
  /**
   * 818. Moves an attachment from one host to another: "if it's equipped, you
   * may attach one of its Equipment to me". Distinct from `attach`, which
   * attaches the source itself.
   */
  | { op: 'attachFrom'; from: Selector; to: Selector }
  /**
   * 186.4. The target becomes a copy of another object: a Reflection token
   * turning into the unit beside it.
   *
   * Modelled by changing which card the object is, rather than by layering a
   * copy on top. Everything downstream reads the card through the index, so a
   * copy is the card it copied in every respect that matters -- and the object
   * keeps its own id, damage and attachments, which is what 186.4 describes.
   */
  | {
    op: 'becomeCopy'; target: Selector; of: Selector;
    /**
     * 818. "... for as long as this is attached to it." The copy is undone
     * when the Equipment that made it comes off, so the card it was is kept
     * and the cleanup loop watches the attachment.
     */
    whileAttached?: boolean;
  }
  /** 130.4. Moves an object to another player's control. */
  | {
    op: 'gainControl'; target: Selector; who?: Selector;
    /**
     * 187.4. How long the change lasts. `permanent` is the default; `turn`
     * hands it back in the Expiration Step; `whileSourceOnBoard` hands it back
     * when the card that took it leaves ("you control it until I leave the
     * board").
     */
    until?: 'turn' | 'whileSourceOnBoard';
  }
  /**
   * 185. Puts a battlefield token onto the board, if it is not already there.
   * `enterThere` places the source at it, which is how "add the Baron Pit
   * battlefield token ... If you do, I enter there" reads as one action.
   */
  | { op: 'addBattlefield'; card: string; enterThere?: boolean }
  /**
   * 185. Swaps the battlefield the source is at for a token, keeping the slot
   * and everything standing on it. The index is what locations point at, so it
   * has to stay the same -- only the card underneath changes.
   */
  | { op: 'replaceBattlefield'; card: string }
  /** 355.9. "You may make new choices for it" -- re-pick a chain item's targets. */
  | { op: 'retarget'; target: Selector }
  /** 440. Burn: put cards from the top of a deck into the trash. */
  | {
    op: 'burn'; count: number; who?: Selector;
    /**
     * 440. "When you burn a unit this way, do this: ..." Runs once for each
     * burned card that matches, with it as the referent. Part of the burn
     * rather than a trigger of its own: nothing announces which cards a
     * particular burn put in the trash.
     */
    forEachBurned?: { filter: ObjectFilter; then: Instruction[] };
  }
  | { op: 'spendXp'; amount: number }
  /** 437. Prevent the next N damage that would be dealt to the target. */
  | { op: 'prevent'; amount: number; target: Selector }
  /** Runs `then` only if `cond` holds. Compiles "If you do, ..." and Legion. */
  | { op: 'if'; cond: Condition; then: Instruction[]; else?: Instruction[] }
  /** Executes the block N times (820, and "Do this twice"). */
  | { op: 'repeat'; times: number; body: Instruction[] }
  /**
   * "For each friendly unit, you may spend its buff to ready it." A loop over
   * the objects a filter names, running the body once with each as the chosen
   * referent -- which is what "its" and "it" refer back to.
   */
  | { op: 'forEach'; of: ObjectFilter; body: Instruction[] }
  /**
   * 705. "Then for each unit this kills, do this: Gain 1 XP." The count is how
   * many the damage just before it finished off, which no board state records
   * -- only the running context knows.
   */
  | { op: 'perKill'; body: Instruction[] }
  /**
   * 705. "Then for each unit this kills, do this: Gain 1 XP." The count is how
   * many the damage just before it finished off, which no board state records
   * -- only the running context knows.
   */
  | { op: 'perKill'; body: Instruction[] }
  /**
   * 204.3. A cost inside an instruction: "[do X] to [do Y]". The effect only
   * happens if the cost is actually paid, which is what separates this from a
   * plain sequence of instructions.
   */
  | { op: 'payTo'; cost: ActivationCost; then: Instruction[] }
  /**
   * 204.3. "Pay any amount of [A] to deal that much damage." The amount is the
   * player's choice; the instructions that follow read it back as
   * `paidAmount`, which is the only way "that much" can mean anything.
   */
  | { op: 'payAnyAmount'; of: 'energy' | 'power'; then: Instruction[] }
  /** Records a target for later instructions to refer to as "it". */
  | { op: 'choose'; target: Selector }
  /**
   * 417.6.b.3. Two units deal damage equal to their Might to each other. The
   * damage comes from the units, not from the spell that arranged it.
   */
  | { op: 'fight'; a: Selector; b: Selector }
  /** Puts a card facedown at a battlefield (811). */
  | { op: 'hide'; filter?: ObjectFilter }
  /**
   * 355.3. A bulleted list of modes. The choice is made when the item is
   * finalized; `distinct` marks the "one you have not chosen this turn" form.
   */
  | {
    op: 'chooseMode'; modes: Instruction[][]; count?: number; distinct?: boolean;
    /**
     * 355.3. The printed words for each mode, for a client to offer.
     *
     * Carried rather than reconstructed: a mode is a clause of the card's own
     * text, and nothing downstream can recover which clause an instruction
     * list came from. Absent where the modes were inferred rather than
     * printed as a list, and a client falls back to numbering them.
     */
    labels?: string[];
  }
  /**
   * 355.3. "Do one of the following based on its domain" / "based on the
   * discarded card's type". Not a `chooseMode`: nobody chooses, the referent
   * decides -- so the branch is picked by reading it rather than by the
   * controller.
   */
  | {
    op: 'switchOn'; on: 'domain' | 'type';
    cases: { key: string; then: Instruction[] }[];
  }
  /**
   * 432. "Swap the [M] of two units at the same battlefield this turn." Two
   * modifications of opposite sign, so it stacks and expires like every other
   * Might change rather than replacing the printed values.
   */
  | { op: 'swapMight'; target: Selector; duration: Duration }
  /** 432. Doubles a numeric attribute for a duration. */
  | { op: 'double'; attribute: 'might'; target: Selector; duration: Duration }
  /**
   * 356. Cost reduction applied while playing something. `scope` says what it
   * applies to: this card as it is being played, or the next thing of a kind.
   */
  /** 424. An opponent shows their hand. */
  | { op: 'revealHand'; who?: Selector }
  /**
   * 811. "You can look at their facedown cards this turn." The engine keeps no
   * hidden information from itself, so there is nothing to reveal -- but the
   * looking is announced, which is what a UI needs and what keeps the sentence
   * accounted for rather than silently dropped.
   */
  | { op: 'lookAtFacedown'; who?: Selector }
  /** 195. An effect that simply ends the game. */
  | { op: 'winGame'; who?: Selector }
  /** 314. "Take a turn after this one." */
  | { op: 'extraTurn' }
  /** 195. "Increase the points needed to win the game by 1." */
  | { op: 'victoryScore'; by: number }
  /**
   * 355.9.b. "It can't be chosen by enemy spells and abilities this turn."
   * The timed form of the `cantBeChosen` passive.
   */
  | { op: 'shield'; target: Selector; duration: Duration }
  /** 705. "Prevent the next 7 damage that would be dealt to it this turn." */
  | { op: 'preventDamage'; target: Selector; amount: number }
  /** 705. "Double all damage that would be dealt to it this turn." */
  | { op: 'multiplyDamage'; target: Selector; factor: number }
  /** 363.3. "This turn, that unit has '...'". An ability lent for the turn. */
  | { op: 'grantAbility'; target: Selector; ability: Ability }
  /**
   * 355.12. "Deal 6 to it unless its controller has you draw 2." The target's
   * controller chooses which way it goes. Nothing here can prompt, so they
   * take the alternative when the damage would kill the unit and eat it
   * otherwise -- the choice a player makes when the price is a card or a
   * creature.
   */
  | { op: 'dealUnless'; target: Selector; amount: number; instead: Instruction[] }
  /** 144. "They can't move it this turn." */
  | { op: 'preventMove'; target: Selector }
  /** 133.8. "I gain that tag" -- a tag added to an object during the game. */
  | { op: 'grantTag'; target: Selector; tag: string }
  /**
   * 432. "Increase its Might to the Might of another friendly unit" and "its
   * base Might becomes 5 this turn".
   *
   * Written as a modification of the same amount rather than as a replacement
   * of the printed value, so it stacks and expires like every other Might
   * change (317.2.c). `raiseOnly` is the "increase ... to" wording, which never
   * lowers a unit that is already bigger.
   */
  | {
    op: 'setMight'; target: Selector; duration: Duration;
    amount?: number; from?: Selector; raiseOnly?: boolean;
  }
  /**
   * Creates an effect that lasts the rest of this turn (363.2). Kept separate
   * from the passive system, which only describes what a permanent radiates
   * while it is on the board.
   */
  | {
    op: 'thisTurn'; effect: TurnEffect;
    /**
     * Whose turn effect it is. Defaults to the controller; "its controller
     * can't play spells this turn" puts it on someone else.
     */
    who?: Selector;
  }
  /**
   * 363.3. Schedules its instructions for a later moment in the turn cycle.
   * "at the end of this turn" is the same turn; "your next Main Phase" is the
   * next one, which is why the scheduler records the turn it was made on.
   */
  | {
    op: 'delay';
    /** 363.3. `hold` waits on a player holding a battlefield, not on a phase. */
    when: 'startOfPhase' | 'endOfTurn' | 'hold';
    phase?: Phase;
    next?: boolean;
    /** Whose moment it is, when that is not the effect's own controller. */
    who?: 'opponent';
    /** Carry what has been chosen, so "return it" still names something. */
    keepChosen?: boolean;
    effects: Instruction[];
  }
  /**
   * 363.3. Arms a one-shot for the rest of this turn: the next death, the next
   * damage, or the next unit played. `target` names what is being watched for
   * the first two and is ignored for the third.
   */
  | {
    op: 'armNextTime'; on: 'dies' | 'dealtDamage' | 'unitPlayed' | 'winsCombat';
    target?: Selector; effects: Instruction[];
  }
  /** No-op placeholder so a partially understood card is explicit, not silent. */
  | { op: 'unimplemented'; text: string };

export type Condition =
  | { c: 'playedAnotherCardThisTurn' }
  /** True when the damage this instruction just dealt was lethal. */
  | { c: 'killedIt' }
  | { c: 'controlsBattlefield' }
  | { c: 'opponentControlsBattlefield' }
  | { c: 'selfIsMighty' }
  | { c: 'selfEmpowered' }
  /** 441. "While I'm [Empowered] three times" -- a count, not the status. */
  | { c: 'selfEmpoweredTimes'; times: number }
  /** 355.9. "If you've chosen an enemy unit this turn" -- and how many times. */
  | { c: 'chosenEnemyThisTurn'; count: number }
  | { c: 'selfBuffed' }
  /** 141.1.a.1. True while the source is at a battlefield rather than a base. */
  | { c: 'selfAtBattlefield' }
  /** True while the source is the only unit its controller has there. */
  | { c: 'selfAlone' }
  /**
   * 143. "If an enemy unit is alone here" -- exactly one enemy unit at my
   * location.
   *
   * Not `selfAlone`, which asks whether *I* have company: Kha'Zix was
   * compiled onto that and so buffed itself for walking in on its own,
   * whatever it walked in on.
   */
  | { c: 'loneEnemyHere' }
  /**
   * "... if it's the only unit you control there." The same test as
   * `selfAlone`, asked about what the effect chose rather than about its source.
   */
  | { c: 'chosenAlone' }
  /**
   * 355. "If at least one of them has [Temporary]." A test across everything
   * the effect chose, rather than `chosenIs`, which asks it of every one.
   */
  | { c: 'anyChosenIs'; filter: ObjectFilter }
  /** 705. "If I haven't been dealt damage this turn." */
  | { c: 'selfDamagedThisTurn' }
  /**
   * 818. "If this was attached to me this turn." An Equipment asking whether it
   * arrived on its host this turn, which no board state records -- the object
   * has to remember when it was attached.
   */
  | { c: 'attachedThisTurn' }
  /** 315. "If a friendly unit died during your Beginning Phase this turn." */
  | { c: 'unitDiedInBeginningPhase'; side: 'friendly' | 'enemy' | 'any' }
  | { c: 'hasXp'; amount: number }
  | { c: 'targetDied' }
  | { c: 'didIt' }
  | { c: 'discardedThisTurn' }
  | { c: 'handAtMost'; count: number }
  | { c: 'handAtLeast'; count: number }
  /** "If you have exactly 4 cards in hand" -- an equality, not a floor. */
  | { c: 'handExactly'; count: number }
  /** "... and exactly 4 units at battlefields." */
  | { c: 'countExactly'; filter: ObjectFilter; count: number }
  /** True when the source is the only friendly unit at its location bar one. */
  | { c: 'exactlyOneOtherUnitHere' }
  | { c: 'paidAdditionalCost' }
  | { c: 'selfMightAtLeast'; amount: number }
  /** 133.8. "if you control a Poro" and friends. */
  | { c: 'controlsTag'; tag: string }
  /** 194.3. True while any opponent is within `within` points of winning. */
  | { c: 'opponentNearVictory'; within: number }
  /** The mirror of the above, for "while your score is within N points". */
  | { c: 'selfNearVictory'; within: number }
  /** "If it's a gear, draw it" -- what a preceding reveal turned up. */
  | { c: 'revealedIs'; filter: ObjectFilter }
  /** "If it is stunned, kill it" -- a test on the object already chosen. */
  | { c: 'chosenIs'; filter: ObjectFilter }
  /** "if it has less Might than me" -- compares the chosen object to the source. */
  | { c: 'chosenMightLessThanSelf' }
  /** "while there's a stunned enemy unit here" -- anything on the board matching. */
  | { c: 'exists'; filter: ObjectFilter }
  /** 460. "unless I'm in combat" -- the source carries a combat designation. */
  | { c: 'selfInCombat' }
  /**
   * 461. "During showdowns here." A showdown is global, but it is staged at a
   * battlefield, so a battlefield's own text asks about its own.
   */
  | { c: 'showdownHere' }
  /**
   * 463. "While I'm attacking with another unit" -- attacking, and not the
   * only attacker. Distinct from `selfAlone`, which is about the whole
   * location rather than the attack.
   */
  | { c: 'selfAttackingWithAnother' }
  /** "if it's your turn" -- the source's controller is the turn player. */
  | { c: 'isYourTurn' }
  /** "if you have 3+ units here" -- a count of matching board objects. */
  | { c: 'countAtLeast'; filter: ObjectFilter; count: number }
  /** "if a friendly unit died this turn" (705). */
  | { c: 'unitDiedThisTurn'; side: 'friendly' | 'enemy' | 'any' }
  /** "if it's your Beginning Phase" -- which phase the turn is in (315). */
  | { c: 'isPhase'; phase: Phase }
  /** "each player's first Beginning Phase" -- before they have taken a turn. */
  | { c: 'onFirstTurn' }
  /** 152. A battlefield asking whether its own controller is you. */
  | { c: 'controlsSelfBattlefield' }
  /** "if you've played a spell this turn" -- what was played, not what is out. */
  | {
    c: 'playedThisTurn'; filter: ObjectFilter; count?: number;
    /** Whose play it was. Defaults to the asker. */
    who?: 'opponent';
  }
  /** 811. "if you control a facedown card at a battlefield". */
  | { c: 'controlsFacedown' }
  /** 108. "... to your Champion Zone if it's empty." */
  | { c: 'championZoneEmpty' }
  /** "if I have moved twice this turn" (144). */
  | { c: 'selfMovedAtLeast'; count: number }
  /**
   * The negation of another condition. Used for tiered text: "[Level 3] I cost
   * [2] less. [Level 6] I cost [4] less instead." -- the lower tier applies
   * only while no higher one does.
   */
  | { c: 'not'; cond: Condition }
  /** Two tests under one "if": "exactly 4 cards in hand and exactly 4 units". */
  | { c: 'all'; conds: Condition[] }
  /** "if you control 4 or fewer runes" -- runes in play, not in the deck. */
  | { c: 'runesAtMost'; count: number }
  | { c: 'runesAtLeast'; count: number }
  /** "if you've spent [4] or more this turn" (204). */
  | { c: 'spentAtLeast'; energy: number; power: number }
  /** "if you assigned 5 or more excess damage" (705.4). */
  | { c: 'assignedExcess'; amount: number }
  /** "if your other units have total [M] 5 or more" -- the sum, not a count. */
  | { c: 'totalMightAtLeast'; filter: ObjectFilter; amount: number }
  /** "if you've gained XP this turn". */
  | { c: 'gainedXpThisTurn' }
  /**
   * 471. "If I didn't conquer this turn" -- about this card, not its
   * controller: an Equipment asks whether the unit it is on conquered.
   */
  | { c: 'selfConqueredThisTurn' }
  /**
   * 349. "If you played this from your hand" -- where this card came from,
   * which matters for the cards that can also be played from trash or hidden.
   */
  | { c: 'selfPlayedFromHand' }
  /** "then kill it if it has 3 [M] or less" -- about what was chosen. */
  | { c: 'chosenMightAtMost'; amount: number }
  /**
   * "if you revealed a Bird, Cat, Dog, or Poro" -- about the cards this effect
   * has just looked at, which only the running effect knows.
   */
  | { c: 'revealedTag'; tags: string[] }
  /** "if you control fewer runes than an opponent" -- a comparison, not a count. */
  | { c: 'fewerRunesThanOpponent' }
  /**
   * "if you've played a non-token unit, a non-token gear, and a spell this
   * turn". Every filter must be satisfied by a *different* card played this
   * turn, which is why it is one condition rather than several `playedThisTurn`
   * gates and'd together.
   */
  | { c: 'playedEachThisTurn'; filters: ObjectFilter[] }
  /**
   * "if your units have all of the following tags among them". `count` is how
   * many of the listed tags must be present; the grammar sets it to all of
   * them, since "all" is the only form the cards print.
   */
  | { c: 'unitsHaveTags'; tags: string[]; count: number };

// -- abilities ---------------------------------------------------------------

/** 383. What makes a triggered ability trigger. */
export type Trigger =
  | { on: 'played'; self: true }
  | { on: 'dies'; self: true }
  | { on: 'attacks' }
  | { on: 'defends' }
  | { on: 'attacksOrDefends' }
  | { on: 'conquer' }
  | { on: 'hold' }
  | { on: 'score' }
  /** 'nth' fires only on that numbered move of the turn (e.g. "the third time"). */
  | { on: 'moves'; nth?: number }
  | {
    on: 'startOfPhase'; phase: 'beginning' | 'main' | 'ending';
    /**
     * "At the start of *each player's* first Beginning Phase" -- a battlefield
     * watching both sides, not only its controller's turn. `first` narrows it
     * to that player's opening turn.
     */
    anyPlayer?: boolean; first?: boolean;
  }
  | { on: 'endOfTurn' }
  | { on: 'startOfCombat' }
  | {
    on: 'endOfCombat';
    /**
     * 463. "When a combat that I was in ends" -- only the combat at the
     * watcher's own battlefield. Without this the trigger fires on every
     * combat its controller is in, wherever it happens.
     */
    here?: boolean;
  }
  | { on: 'playerPlaysCard'; filter?: ObjectFilter }
  | { on: 'becomesEmpowered'; self: true }
  | { on: 'youEmpower'; other?: boolean }
  | { on: 'becomesBuffed'; self: true }
  | { on: 'youBuff' }
  | { on: 'unitDies'; filter?: ObjectFilter }
  | { on: 'youRecycle' }
  | { on: 'youDiscard' }
  /**
   * "When this is discarded" -- the card itself going to the trash from hand,
   * which is not the same event as its controller discarding something.
   */
  | { on: 'discarded'; self: true }
  | { on: 'youDraw' }
  /**
   * 355.9. Something was chosen as a target. With `self` it is the source
   * being chosen; with a filter it is any matching object, which is what
   * "When you choose a friendly unit" watches.
   */
  /**
   * 355.9. `by` is who did the choosing, and it is not decoration.
   *
   * "When **you** choose or ready me" and "when **a player** chooses a unit
   * here" are different sentences that both watch the same event. Without this
   * they compiled to the same trigger, and Irelia -- "when you choose or ready
   * me, give me +1 [M]" -- grew every time an *opponent* targeted her, which is
   * the opposite of what the card rewards.
   */
  | { on: 'chosen'; self: true; by?: 'you' }
  | { on: 'chosen'; filter: ObjectFilter; by?: 'you' }
  | { on: 'readied'; self: true }
  | { on: 'youStun' }
  | { on: 'takesDamage'; self: true }
  | { on: 'youGainPoints' }
  /** 812-style counting: "when you play your second card in a turn". */
  | { on: 'nthCardPlayed'; nth: number }
  | { on: 'mightReaches'; amount: number }
  /** 143.2. "When a unit you control becomes [Mighty]" -- crossing 5 Might. */
  | { on: 'unitBecomesMighty'; filter?: ObjectFilter }
  /** 144. An opponent moved a unit. `elsewhere` excludes the watcher's own location. */
  | { on: 'opponentMoves'; elsewhere?: boolean }
  | { on: 'enemyAttacksYourBattlefield' }
  | { on: 'friendlyWouldDie'; filter?: ObjectFilter }
  | { on: 'youAttachEquipment'; self?: boolean }
  | { on: 'youBanish' }
  | { on: 'youChannel' }
  | { on: 'playedFromHidden'; self: true }
  /** 811. "When you hide a card." */
  | { on: 'youHide' }
  /**
   * 424. "As I'm revealed from your deck, [Add] [2]." The card itself being
   * turned face up, which is not the same event as its controller revealing
   * something.
   */
  | { on: 'revealed'; self: true }
  /** 447. "When you spend a buff." */
  | { on: 'youSpendBuff' }
  /**
   * 414. "When you use an activated ability of a gear." The filter is on the
   * ability's *source*, which is what "of a gear" describes.
   */
  | { on: 'youUseAbility'; filter?: ObjectFilter }
  /** 432. "When an opponent scores." Scoped by controller, like every opponent trigger. */
  | { on: 'opponentScores' }
  /** 375. "When a unit here is returned to a player's hand." */
  | { on: 'returnedToHand'; filter?: ObjectFilter }
  /**
   * 144. A unit leaving somewhere, watched by a third party. `fromHere` is the
   * watcher's own location, which no object filter can express.
   */
  | { on: 'unitMoves'; filter?: ObjectFilter; fromHere?: boolean }
  /**
   * 144. "When you move an enemy unit" -- about the *mover*, not the moved.
   * `unitMoves` cannot express it: that one is announced under the moved
   * unit's controller, so an enemy unit you move is announced under them.
   */
  | { on: 'youMoveUnit'; filter?: ObjectFilter }
  /** 465. "When you kill a stunned enemy unit" -- narrowed by what died. */
  | { on: 'youKillUnit'; filter?: ObjectFilter };

export interface ActivationCost {
  /** 414. The [E] symbol: exhaust this permanent. */
  exhaust?: boolean;
  energy?: number;
  power?: number;
  powerDomain?: Domain | 'any' | 'self';
  /** "Recycle 1 from your trash:" style costs. */
  recycleFromTrash?: number;
  /**
   * 416. "Recycle a unit from your trash" -- a recycle the card narrows, not
   * just a count. Without it the price reads as "recycle any card", which is
   * cheaper than the card prints.
   */
  recycleFromTrashFilter?: ObjectFilter;
  discard?: number;
  /**
   * 203.1. "Discard a spell" -- a discard the card names, not just a count.
   * Without this the cost reads as "discard any one card", which is a cheaper
   * price than the card prints.
   */
  discardFilter?: ObjectFilter;
  /** "Kill this," as a cost. */
  killSelf?: boolean;
  /** 416. "Recycle this:" -- the second ability of every basic rune. */
  recycleSelf?: boolean;
  /**
   * 447. "Banish this:" -- gone for the game rather than to the trash, which
   * is a strictly steeper price than `recycleSelf` and so is its own part.
   */
  banishSelf?: boolean;
  /** 440. "[Burn 1] to ..." -- cards off the top of the Main Deck as a price. */
  burn?: number;
  /** 441. "Disempower this, [1], [E]:" -- spends the source's Empowered status. */
  disempowerSelf?: boolean;
  /** 426. "Spend my buff:" -- removes the source's buff counter as a cost. */
  spendBuff?: boolean;
  /** 441. "Kill this" / empower costs that consume the source's status. */
  spendEmpower?: boolean;
  spendXp?: number;
  /**
   * 206.1. "This ability costs [1] less for each friendly gear." The reduction
   * is worked out when the ability is offered and again when it is paid, so it
   * always reflects the board at that moment.
   */
  energyLessPer?: { amount: number; from: DynamicAmount };
  /**
   * 206.1. "This ability costs [3] less if you control 4 or fewer runes." A
   * flat reduction behind a gate rather than one that scales, so it cannot be
   * written as `energyLessPer`.
   */
  energyLessIf?: { amount: number; cond: Condition };
  /**
   * 203.1. A cost paid by doing something to another object you control:
   * "As an additional cost to play me, kill a friendly unit."
   */
  sacrifice?: {
    action: 'kill' | 'exhaust' | 'return' | 'spendBuff';
    filter: ObjectFilter;
    count: number;
  };
}

/** When an activated ability may be used (806, 813). */
export type Timing = 'main' | 'action' | 'reaction';

export type Ability =
  /** `once` limits the ability to one trigger per turn (371). */
  | {
    kind: 'triggered'; trigger: Trigger; effects: Instruction[];
    optional?: boolean; once?: boolean;
    /**
     * 375. Who acts when this fires. Normally the watcher's controller; a
     * battlefield says otherwise -- "When a player plays a spell, *they* may
     * give a unit they control here +1 [M]" is an offer to whoever played, not
     * to whoever holds the battlefield, which may be nobody at all.
     */
    byEventPlayer?: boolean;
  }
  /**
   * 356.2. A cost paid while playing this card, on top of its printed cost.
   * Optional ones are the player's choice; a following "if you paid the
   * additional cost" reads whether they took it.
   */
  | {
    kind: 'additionalCost'; cost: ActivationCost; optional: boolean;
    /**
     * 356.2. "Spend any number of buffs as an additional cost. Reduce my cost
     * by [O] for each buff you spend." The player picks how many, and each one
     * buys a fixed discount -- so the price and the reduction are one thing,
     * not a cost and a separate modifier.
     *
     * Nothing here can prompt, so the play takes the fewest that make the card
     * affordable: spending more is a pure loss.
     */
    variable?: {
      of: 'buff' | 'friendlyUnit';
      per: { energy: number; power: number; domain?: Domain | 'any' };
    };
  }
  | {
    kind: 'activated'; key: string; cost: ActivationCost; timing: Timing;
    effects: Instruction[];
    /**
     * 818/827. Which keyword this ability is the shorthand for, when it is
     * one. `key` cannot carry it: keys are renumbered per card to keep them
     * distinct, and Weaponmaster has to find the Equip ability by meaning.
     */
    from?: 'equip' | 'empower';
    /** 371. "Use only once per turn." */
    once?: boolean;
    /**
     * 371. "Use only if you've chosen an enemy unit this turn."
     *
     * A restriction on *using* the ability, not on what it does. Gating the
     * effects instead would let a player pay the cost and get nothing, which
     * is a different card.
     */
    useIf?: Condition;
  }
  /** 363. Continuous, always on while the source is on the board. */
  | { kind: 'passive'; effects: PassiveEffect[] }
  /** 353. Instructions run as the card resolves. Spells are all of this shape. */
  | { kind: 'spell'; effects: Instruction[] };

/** A static modification a permanent applies while it is on the board. */
export type PassiveEffect =
  | { p: 'mightAura'; amount: number; filter: ObjectFilter; min?: number }
  | { p: 'keywordAura'; keyword: Keyword; value?: number; filter: ObjectFilter }
  | {
    p: 'costModifier'; energy?: number; power?: number; filter: ObjectFilter;
    /** 356. "...to a minimum of [1]" -- a floor the discount cannot go under. */
    min?: number;
  }
  /**
   * 471.2. "Your conquer effects for conquering here trigger an additional
   * time." A doubler on one class of score trigger, and only at the
   * battlefield the source is standing at.
   */
  | { p: 'extraScoreTrigger'; on: 'conquer' | 'hold' | 'score' }
  /**
   * 471. "My hold effects are also conquer effects, and vice versa." The two
   * score triggers become aliases of each other *on this card only*, which is
   * why it is a property of the watcher rather than of the event.
   */
  | {
    p: 'holdAndConquerAlias';
    /**
     * 471. Without a filter the alias is about the card printing it. With one
     * it is an aura: a battlefield saying "when you hold here, activate the
     * conquer effects of units here" makes the swap for its neighbours.
     */
    filter?: ObjectFilter;
  }
  /** 383. "Your [Deathknell] effects trigger an additional time." */
  | { p: 'extraKeywordTrigger'; keyword: Keyword }
  /**
   * 356. A card discounting itself from hand: "I cost [1] less for each card in
   * your trash."
   *
   * Separate from `costModifier` because that one is read off the board, and a
   * card in hand is not on the board -- these cards compiled to a no-op
   * instruction and were fully inert while counting as modelled. `per` makes
   * the discount a multiple of something counted at the moment it is asked.
   */
  | {
    p: 'selfCostModifier'; energy: number; power?: number; per?: DynamicAmount;
    /**
     * 356. "This costs [2] less if you choose a Bird, Cat, Dog, or Poro." The
     * discount depends on what this play chooses, which is settled only once
     * targets are picked -- the same moment the Deflect tax is priced.
     */
    ifChoosing?: ObjectFilter;
    /** 356. "...to a minimum of [1]" -- a floor the discount cannot go under. */
    min?: number;
    /**
     * 356.2. "If you do, I cost [1] less for each Energy it costs and [Y] less
     * for each Power it costs" -- the discount is read off the card killed to
     * pay this one's additional cost.
     *
     * Its own field rather than a `per`, because it changes both halves of the
     * price at once and the number is only known once the victim is chosen,
     * which is after the play has begun.
     */
    perSacrificed?: boolean;
    /**
     * 356. "I cost [2] less to play from anywhere other than your hand."
     * Settled by the play machinery, which is the only thing that knows where
     * the card is being played from.
     */
    ifNotFromHand?: boolean;
    /**
     * 356.2. "If you do, reduce my cost by [2]" -- the discount that follows an
     * optional additional cost. Not part of `totalCost`, because whether it
     * applies is a choice made at the moment of playing.
     */
    ifPaidAdditional?: boolean;
  }
  /** 143.4 is the default; this overrides it in the other direction. */
  | { p: 'entersExhausted' }
  /** 414. "I can't be readied." Nothing untaps this, by any means. */
  | { p: 'cantBeReadied' }
  /**
   * 137.3. "Each Equipment attached to me gives double its base Might bonus."
   * A multiplier on what this object's attachments contribute, which is not a
   * `mightAura`: the amount depends on each attachment, not on the host.
   */
  | { p: 'attachedMightMultiplier'; factor: number }
  /**
   * 349. "I can't be played on your first, second, or third turns." A standing
   * restriction on playing this card, which only the play machinery can test --
   * the card is still in hand, so no board-read passive would ever see it.
   */
  | { p: 'cantPlayBeforeTurn'; turn: number }
  | {
    p: 'entersReady';
    /**
     * 356.2. "If you do, I enter ready" -- gated on the optional additional
     * cost having been taken. Not a `conditional` wrapper, because that is
     * judged from the board and whether the extra cost was paid is not on it.
     */
    ifPaidAdditional?: boolean;
  }
  /** 355.9.b. Removes the source from the legal targets of enemy effects. */
  | {
    p: 'cantBeChosen'; by?: 'enemy' | 'any';
    /**
     * 355.7. Without a filter this protects the source. With one it is an
     * aura: "your units here with less [M] than me can't be chosen by enemy
     * spells and abilities" -- said by a card about its neighbours.
     */
    filter?: ObjectFilter;
  }
  /**
   * 144. "I can't be moved by enemy spells and abilities." A prohibition on
   * being moved *by an effect*; the unit still moves itself as normal, which
   * is why this is separate from `cantBeChosen`.
   */
  | { p: 'cantBeMoved'; by?: 'enemy' | 'any' }
  /** 441. "I can be [Empowered] up to three times." Raises the default cap of 1. */
  | { p: 'empowerLimit'; times: number }
  /**
   * 485. "Players only channel 1 rune at the start of their Channel Phase."
   * A cap on the phase's own channelling, not on channelling generally -- a
   * card that says "channel 2 runes" is untouched.
   */
  | { p: 'channelCap'; runes: number }
  /**
   * 820. "Your spells have [Repeat] [2][P]." Grants the keyword *and* its
   * price -- a Repeat without a cost is not a Repeat at all.
   */
  | {
    p: 'grantsRepeat'; filter: ObjectFilter;
    /**
     * Named `price` rather than `cost`: an ActivationCost is a list of parts a
     * payment path settles, and this is a plain resource pair like `flowCost`.
     */
    price: { energy: number; power: number; domain?: Domain | 'any' };
  }
  /** 820. "Friendly [Repeat] costs cost [1] less." */
  | { p: 'repeatCostModifier'; energy: number }
  /**
   * 809. "Ignore [Deflect] while paying this spell's cost." Printed on the
   * spell itself, so the tax is skipped for this card alone -- distinct from
   * the battlefield form, which lifts it for everyone playing there.
   */
  | { p: 'ignoreDeflect' }
  /**
   * 471. "If a player would score 1 point from conquering or holding during
   * their first or second turn, they draw 1 instead." A replacement on
   * scoring, in the same family as `replaceDeath` -- the point never happens
   * and something else does. `untilTurn` counts that player's own turns.
   */
  | { p: 'replaceScore'; untilTurn: number; effects: Instruction[] }
  /**
   * 466. "If a combat where you are the attacker ends in a tie, recall ALL
   * units instead." A tie is the case where neither side is left alone at the
   * battlefield; the printed rule recalls only the attackers (466.1.a.2), and
   * this replaces that with recalling everyone.
   */
  | { p: 'replaceTie'; asAttacker: boolean }
  /**
   * 103.2.b. "Your deck can have any number of cards named Spiderling." Lifts
   * the three-copy cap for this card. Read by `buildDeck` rather than by the
   * engine -- it is a rule about legal decks, not about play.
   */
  | { p: 'unlimitedCopies' }
  /**
   * 829. "Spells with [Flow] you play from your trash cost [2] less." A
   * discount on the Flow price specifically -- `costModifier` reads the
   * printed cost, which a Flow play never pays.
   */
  | { p: 'flowCostModifier'; energy: number; min?: number }
  /**
   * 809. "Players ignore [Deflect] while paying for spells and abilities
   * choosing something here." The battlefield form of `ignoreDeflect`: it
   * lifts the tax for everyone, but only for things aimed at its own location.
   */
  | { p: 'noDeflectHere' }
  /**
   * 827. "[Empower] costs of your units here cost [1] or [A] less." A discount
   * on the Empower price, which is an activated ability's cost rather than a
   * card's -- `costModifier` reads printed card costs and would never see it.
   *
   * `either` is the "[1] or [A]" form, resolved the way the other either-costs
   * are: the half the pool is short of.
   */
  /**
   * 356. "The first friendly non-token gear played each turn costs [1] less."
   * A discount that applies once a turn, to whichever matching thing goes
   * first. `what` says whether it counts cards played or abilities used --
   * two different tallies, and a card names one or the other.
   */
  | {
    p: 'firstEachTurnDiscount'; what: 'card' | 'ability';
    energy: number; filter: ObjectFilter;
  }
  | {
    p: 'abilityCostModifier'; from: 'empower' | 'equip';
    energy: number; power: number; either?: boolean;
    filter: ObjectFilter;
  }
  /** 133.8. "I am a Mech." A tag the card has without printing it in its tag line. */
  | { p: 'selfTag'; tag: string }
  /**
   * 429. "Your Gold [Add] an additional [1]." An aura over what somebody
   * else's resource ability produces, so it is read where the resource is
   * added rather than where the ability lives.
   */
  | { p: 'extraResource'; filter: ObjectFilter; energy?: number; power?: number }
  /**
   * 471. "Increase the points needed to win the game by 1." A standing
   * modification rather than a one-off: it is printed on a battlefield, which
   * is in play from setup and never "enters", so there is no moment for an
   * instruction to run -- and it must lift again if the battlefield is
   * replaced.
   */
  | { p: 'victoryScoreMod'; by: number }
  /**
   * 705. "Any amount of your damage is enough to kill enemy units." The
   * standing form of the `anyDamageKills` turn effect. Like that one, it does
   * not track who dealt the damage -- `hasLethalDamage` is asked about a unit,
   * not about an instance of damage -- so `filter` is what keeps it off the
   * controller's own units.
   */
  | { p: 'anyDamageKills'; filter: ObjectFilter }
  /**
   * 356. "Your spells that choose me cost [1] or [A] less."
   *
   * The mirror image of Deflect (809.1.c), which taxes an opponent for choosing
   * this card. It cannot be a `costModifier`: that one is read off the board
   * against a card definition, and whether a spell chooses *this* object is not
   * a property of the definition -- it is known only once targets are picked,
   * which is where the Deflect tax is priced too.
   *
   * `either` is the "[1] or [A]" form: one reduction or the other, the
   * controller's choice. Nothing here can prompt, so the play machinery takes
   * whichever the pool can actually cover, preferring Energy on a tie.
   */
  /**
   * 356.2. "Optional additional costs you pay cost [1] or [A] less." A discount
   * on the *extra* price a card asks for, not on the card. Held apart from
   * `costModifier` for the same reason: nothing about a card definition says
   * what its additional cost will be once this aura is out.
   */
  | { p: 'extraCostDiscount'; energy: number; power: number; either?: boolean }
  | {
    p: 'chosenDiscount'; energy: number; power: number; either?: boolean;
    /** Whose spells: "your" means the card's own controller. */
    side: 'friendly' | 'enemy' | 'any';
    /**
     * 356. Which of the play's choices earn the discount. Without one the
     * discount is about the card carrying it -- "spells that choose *me*".
     * With one it is a bystander's rule about others: a battlefield saying
     * "each spell that chooses one or more units here that are friendly to it
     * costs [A] less".
     */
    filter?: ObjectFilter;
  }
  /** 137.2 allows a unit one buff at a time; this lifts that cap. */
  | { p: 'multiBuff' }
  /**
   * 371. "I have all [E] abilities of all friendly legends, units, and gear."
   *
   * The mirror of `grantsAbility`: that one pushes an ability outward onto
   * whatever a filter names, this one pulls every matching permanent's
   * abilities inward onto the card printing it. Two different sentences, and
   * a card cannot express one as the other -- "I have yours" says nothing
   * about who else has it.
   */
  | {
    p: 'gainsAbilitiesOf';
    filter: ObjectFilter;
    /**
     * Which of the other card's abilities come across. `exhaust` is
     * Heimerdinger's "all [E] abilities"; leaving it off is "copy that unit's
     * text", which takes everything printed.
     */
    only?: 'exhaust';
  }
  /**
   * 704. "If you would play a token unit while I'm at a battlefield, you may
   * play that token and an additional copy of it instead."
   *
   * A replacement rather than a trigger: the second token arrives with the
   * first, not after it, which is what lets both be counted by whatever the
   * effect that made them goes on to say ("then ready two of them").
   *
   * `oncePerTurn` is tracked on the permanent holding the passive, so two
   * copies of the card each get their own use.
   */
  | { p: 'duplicateTokens'; oncePerTurn?: boolean }
  /**
   * 424. "If you would reveal cards from a deck, look at the top card first.
   * You may recycle it. Then reveal those cards."
   *
   * A replacement on revealing, so it has to sit in front of the reveal rather
   * than trigger off it: the point is to change *what* gets revealed.
   */
  | { p: 'peekBeforeReveal' }
  /**
   * 316.4. "Skip your Draw Phase." A phase that happens but does nothing,
   * rather than one that is removed: everything watching for the phase still
   * sees it arrive.
   */
  | { p: 'skipDraw'; who: 'you' | 'opponents' }
  /**
   * 447. "If a card would go to your trash from anywhere other than your Main
   * Deck, banish it instead." A replacement on where a card lands, so it has
   * to sit inside the move rather than react to it.
   */
  | { p: 'trashBecomesBanish'; who: 'you' | 'opponents'; exceptFromDeck?: boolean }
  /**
   * 144.3. "Opponents must pay [A] for each unit beyond the first to move
   * multiple units to my battlefield at the same time."
   *
   * A price on the standard move, which is otherwise paid only by exhausting.
   * It bites on a *group* move (144.3) and on nothing else, so it is charged
   * per unit past the first rather than per unit.
   */
  /**
   * 356.2. "Any player may pay [A][A] as an additional cost to play a Dragon.
   * If they do, they play it to this battlefield."
   *
   * An optional additional cost one permanent offers on *other* players' cards,
   * as opposed to `additionalCost`, which a card prints about itself. Taking it
   * is what puts the card at the offering permanent's battlefield, so the price
   * and the destination are one thing rather than two.
   */
  | {
    p: 'grantsAdditionalCost';
    filter: ObjectFilter;
    who: 'all' | 'opponents' | 'you';
    energy: number;
    power: number;
    /** Where the card lands when the offer is taken. */
    to: 'here';
  }
  | {
    p: 'moveTax';
    who: 'opponents' | 'all';
    where: 'here';
    energy: number;
    power: number;
  }
  /**
   * 704. A replacement for what a chosen effect would do to this permanent:
   * "if a spell or ability that chooses me would stun me, give me -[M], or
   * return me to hand, give me +3 [M] instead".
   *
   * Named by what it catches rather than by a filter, because these are three
   * different instructions rather than three shapes of one -- and a card that
   * catches only some of them must not silently catch the rest.
   */
  | {
    p: 'replaceChosen';
    of: ('stun' | 'mightPenalty' | 'returnToHand')[];
    effects: Instruction[];
  }
  /**
   * 704. A replacement effect for death: the unit does not die, and these
   * instructions happen instead. `filter` says whose death it catches, with
   * the dying unit available to the instructions as "it".
   */
  | {
    p: 'replaceDeath';
    filter: ObjectFilter;
    cond?: Condition;
    /**
     * 204.3. A price for the replacement: "you may pay [A] and exhaust me to
     * ... instead". Unpayable means the replacement does not apply and the
     * unit dies as normal.
     */
    cost?: ActivationCost;
    /**
     * 704. Who pays and who acts. A battlefield saying "its controller may pay
     * [A][A][A] to heal it" offers the replacement to the *dying unit's*
     * controller, not to whoever holds the battlefield -- which may be nobody.
     */
    byVictimController?: boolean;
    effects: Instruction[];
  }
  /** A passive that only applies while a condition holds (824, 828). */
  | { p: 'conditional'; cond: Condition; effects: PassiveEffect[] }
  /** Grants the source itself a keyword, e.g. "[Empowered] I have [Tank]". */
  | {
    p: 'selfKeyword'; keyword: Keyword; value?: number;
    /** "[Assault] equal to the number of enemy units here" -- read live. */
    valueFrom?: DynamicAmount;
  }
  | { p: 'selfMight'; amount: number }
  /** Might that tracks a changing quantity, e.g. "+1 [M] for each point". */
  | { p: 'selfMightFrom'; from: DynamicAmount; per?: number }
  /**
   * 465. Units that deal no combat damage. Without a filter it is the source
   * itself; with one it is an aura over whoever matches.
   */
  | { p: 'noCombatDamage'; filter?: ObjectFilter }
  /** 705. The source cannot be dealt damage at all. */
  /**
   * 705. Damage simply does not land on the source. `by` narrows it to damage
   * from an opponent's spells and abilities; without one, nothing can damage
   * this at all, which is a strictly stronger card.
   */
  | { p: 'cantBeDealtDamage'; by?: 'enemyEffects' }
  /**
   * 811.1.b. Replaces what its controller pays to Hide. The printed price is
   * [A]; a card carrying this makes it something else.
   */
  | { p: 'hideCost'; energy: number; power: number }
  /**
   * 107.3. "You may hide an additional card here." One face-down card per
   * battlefield is the default; each of these raises the limit at the source's
   * own location by one.
   */
  | { p: 'extraHideSlot' }
  /** 141.1.a.1. The source's activated abilities need it at a battlefield. */
  | { p: 'abilityRequiresBattlefield' }
  /**
   * 822. Widens the locations a unit may be played to. With no filter it
   * speaks for the source alone; with one it is an aura over other units.
   */
  | {
    p: 'playableTo';
    where:
      | 'openBattlefield' | 'anyBattlefield' | 'enemyBattlefield'
      /** 463. "I can be played to a battlefield you're attacking." */
      | 'attackingBattlefield'
      /**
       * 471. "Play me only to a battlefield you conquered this turn." Unlike
       * the others this *narrows* where the card may go rather than widening
       * it, which is why it is checked separately.
       */
      | 'onlyConqueredThisTurn'
      /**
       * 356.2. "You may play me to its battlefield (even if you don't have
       * other units there)" -- said about the unit killed to pay this card's
       * additional cost, so the destination is wherever a unit the cost could
       * name is standing.
       */
      | 'sacrificedBattlefield'
      /**
       * 152. "I can be played to an occupied battlefield if an enemy unit is
       * alone there." Occupied *by a lone enemy*, which is not the same as any
       * enemy battlefield -- two enemies there and the permission lapses.
       */
      | 'battlefieldWithLoneEnemy';
    filter?: ObjectFilter;
  }
  /** 706. Extra damage added to every Deal this player's effects perform. */
  | { p: 'bonusDamage'; amount: number; filter?: ObjectFilter }
  /**
   * 143. "If a spell or ability you control would give -[M] to a unit it
   * chooses, it gives an additional -1 [M]." The mirror of `bonusDamage` for
   * Might reductions: extra on top of every penalty this player's effects hand
   * out, and nothing at all to an increase.
   */
  | { p: 'bonusMightPenalty'; amount: number }
  /**
   * A standing prohibition: "Units can't move to base", "Opponents can't score
   * points". `who` is relative to the source's controller; `where` limits it to
   * the source's own location.
   */
  | {
    p: 'restrict';
    what:
      | 'playCards' | 'score' | 'ready' | 'moveToBase' | 'playUnitsHere'
      /** 811. "Your opponents' [Hidden] cards can't be revealed here." */
      | 'revealHidden'
      /** 133.8. "Opponents can't play spells with that name." */
      | 'playNamedSpell';
    who: 'self' | 'you' | 'opponents' | 'all';
    where?: 'here';
    /**
     * 470. "Players can't score here until their third turn." The restriction
     * lifts once that player has taken this many turns.
     */
    untilTurn?: number;
  }
  /**
   * 144.3. "Units can move here from anywhere." Movement is otherwise between
   * adjacent places; this opens one destination to every origin.
   */
  | { p: 'openToMoves' }
  /** 425. "This can't be countered." */
  | {
    p: 'cantBeCountered';
    /**
     * 425. Without a filter the card protects itself. With one it is an aura:
     * "your spells and abilities can't be countered" is said by a permanent
     * about everything its controller plays.
     */
    filter?: ObjectFilter;
  }
  /**
   * 816. "Your [Temporary] effects at my battlefield don't trigger." Suppresses
   * the keyword's own end-of-life trigger at this object's location.
   */
  | { p: 'suppressKeyword'; keyword: Keyword }
  /**
   * 363. An ability handed to other objects: `units here have "[E]: gain 1
   * XP."` The ability is a whole `activated` shape, so it is offered, costed
   * and used exactly like one the card printed itself.
   */
  | {
    p: 'grantsAbility'; filter: ObjectFilter;
    ability: Extract<Ability, { kind: 'activated' }>;
  }
  /**
   * 815. "You ignore [Tank] while assigning combat damage here." The keyword
   * still exists; it just stops constraining the order at this location.
   */
  | { p: 'ignoreKeywordInCombat'; keyword: Keyword };

/**
 * The compiled rules of one printing. `complete` is false when the compiler had
 * to give up on part of the text, which is what the coverage report counts and
 * what tells a deck builder to avoid the card in a curated set.
 */
export interface CardScript {
  id: string;
  keywords: Keyword[];
  keywordValues: Partial<Record<Keyword, number>>;
  abilities: Ability[];
  /**
   * 829. The Flow cost, when the card prints one. Kept apart from
   * `keywordValues` because it is a resource cost rather than a number, and
   * nothing else on the card records it -- without it the keyword is inert.
   */
  flowCost?: { energy: number; power: number; domain?: Domain | 'any' };
  /**
   * 820. The Repeat cost: an optional extra paid while playing the spell, for
   * its effects to happen twice.
   *
   * Recorded here for the same reason as `flowCost` -- it is a resource price,
   * not a number, so `keywordValues` cannot hold it, and without it the keyword
   * is a word on twenty cards that does nothing.
   */
  repeatCost?: { energy: number; power: number; domain?: Domain | 'any' };
  /**
   * 356.4. A price the card may be played for instead of its printed one:
   * "If you've spent [4] or more this turn, you may play me for [B]", or
   * "[Legion] You may play me from your trash for [3][R]".
   *
   * Kept apart from `flowCost` even though both replace the printed cost: Flow
   * is a keyword with its own banishment rule (829.1.b), while this is plain
   * permission, can be gated on a condition, and can name the hand.
   */
  altCost?: {
    energy: number; power: number; domain?: Domain | 'any';
    from: 'hand' | 'trash';
    /** The permission's gate, when it has one. Unconditional if absent. */
    cond?: Condition;
  };
  complete: boolean;
  /** Text the compiler could not account for, kept for the coverage report. */
  residual?: string;
}

export type ScriptIndex = Record<string, CardScript>;

/** Cards with no script at all still play as vanilla stats. */
export const EMPTY_SCRIPT: CardScript = {
  id: '', keywords: [], keywordValues: {}, abilities: [], complete: false,
};

// -- basic runes -------------------------------------------------------------

/**
 * 164.2. Every Basic Rune has these two abilities. They are not printed on the
 * card, so they are not in the card data and have to be supplied by the rules.
 */
export function basicRuneAbilities(): Ability[] {
  return [
    {
      kind: 'activated',
      key: 'rune:energy',
      cost: { exhaust: true },
      timing: 'reaction',
      effects: [{ op: 'addResource', energy: 1 }],
    },
    {
      kind: 'activated',
      key: 'rune:power',
      cost: { recycleSelf: true },
      timing: 'reaction',
      // [C]: Power of the recycled rune's own domain (164.2.b.1).
      effects: [{ op: 'addResource', power: 1, domain: 'self' }],
    },
  ];
}
